// Collections System Types
// Unified types for collections, replacing lists and decks

import { FSRSParameters } from 'ts-fsrs'

// =================================================================
// BASE ENTITIES
// =================================================================

export type Collection = {
  id: string
  user_id: string
  name: string
  description: string | null
  is_bookmark: boolean
  fsrs_params: FSRSParameters | null
  new_cards_limit: number
  review_limit: number
  rollover_hour: number
  created_at: string
  updated_at: string
}

export type CollectionItem = {
  id: string
  collection_id: string
  dict_entry_id: number
  user_id: string
  created_at: string
}

export type CollectionWithCount = Collection & {
  item_count: number
}

// =================================================================
// CURATED COLLECTIONS
// =================================================================

export type CuratedCollectionBase = {
  slug: string
  level: number
  coverIcon?: string | null
  entryIds?: number[]
  item_count: number
}

export type CuratedCollectionStrings = {
  title: string
  description?: string
  coverLabel?: string
}

export type CuratedCollectionLocaleMap = Record<
  string,
  Record<string, CuratedCollectionStrings>
>

export type CuratedCollectionsFile = {
  generatedAt: string
  source?: string
  collections: Record<string, CuratedCollectionBase>
}

export type CuratedCollection = CuratedCollectionBase & CuratedCollectionStrings

// =================================================================
// IDENTIFIERS & UTILS
// =================================================================

export type CollectionType = 'user' | 'curated' | 'virtual'
export type CollectionId = string

export function getCollectionType(id: CollectionId): CollectionType {
  let decoded: string
  try {
    decoded = decodeURIComponent(id)
  } catch {
    decoded = id
  }

  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  if (uuidRegex.test(decoded) || uuidRegex.test(id)) {
    return 'user'
  }

  if (decoded === 'all' || decoded === 'due_today' || decoded === 'review') {
    return 'virtual'
  }
  if (id === 'all' || id === 'due_today' || id === 'review') {
    return 'virtual'
  }

  return 'curated'
}

export function getCuratedSlug(id: CollectionId): string | null {
  let decoded: string
  try {
    decoded = decodeURIComponent(id)
  } catch {
    decoded = id
  }

  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  if (uuidRegex.test(decoded) || uuidRegex.test(id)) {
    return null
  }
  if (decoded === 'all' || decoded === 'learning' || decoded === 'review') {
    return null
  }
  if (id === 'all' || id === 'learning' || id === 'review') {
    return null
  }

  return decoded || id
}

export function getVirtualType(id: CollectionId): VirtualListType | null {
  let decoded: string
  try {
    decoded = decodeURIComponent(id)
  } catch {
    decoded = id
  }

  if (decoded === 'all' || decoded === 'due_today' || decoded === 'review') {
    return decoded as VirtualListType
  }
  if (id === 'all' || id === 'due_today' || id === 'review') {
    return id as VirtualListType
  }

  return null
}

export type VirtualListType = 'all' | 'due_today' | 'review'

export type CollectionDisplay = {
  type: CollectionType
  id: CollectionId
  slug?: string
  name: string
  description?: string | null
  item_count: number
  is_bookmark?: boolean
  is_default?: boolean
  default_kind?: 'saved_words' | 'sentence_mining' | null
  created_at?: string
  updated_at?: string
  virtualListType?: VirtualListType
  total_cards?: number
  due_cards?: number
  new_cards?: number
  new_available_today?: number
  learn_due?: number
  review_due?: number
  review_due_capped?: number
}

// =================================================================
// CARDS & PROGRESS
// =================================================================

export type Card = {
  id: string
  user_id: string
  dict_entry_id: number | null
  collection_id: string | null
  front: string
  back: string | null
  state: number
  due: string
  stability: number
  difficulty: number
  elapsed_days: number
  scheduled_days: number
  learning_steps: number
  reps: number
  lapses: number
  last_review: string | null
  created_at: string
}

export type CardInfo = {
  dict_entry_id: number
  due: string
  last_review: string | null
  reps: number
  lapses: number
  state: number
  front: string
  back: string | null
}

export type WordStatus = 'new' | 'learning' | 'review'

export type BookmarkInfo = {
  dict_entry_id: number
  bookmarked_at: string
}

export type UserCollectionSettings = {
  id: string
  user_id: string
  curated_slug: string
  fsrs_params: FSRSParameters | null
  new_cards_limit: number | null
  review_limit: number | null
  rollover_hour: number | null
  created_at: string
  updated_at: string
}

// =================================================================
// RPC RESPONSE TYPES (Centralized)
// =================================================================

export type RpcEntryWithCard = {
  entry_id: number
  entry_simplified: string
  entry_traditional: string | null
  entry_pinyin_display: string
  entry_zhuyin_display: string | null
  entry_json_data: unknown
  entry_frequency: number
  entry_radical: number | null
  card_due: string | null
  card_last_review: string | null
  card_reps: number | null
  card_lapses: number | null
  card_state: number | null
  card_front: string | null
  card_back: string | null
  item_created_at?: string
  // Translation fields (from entry_translations)
  definitions?: string[] | string
  examples?: unknown[] | string
}

export type RpcCollectionMeta = {
  id: string
  user_id: string
  name: string
  description: string | null
  is_bookmark: boolean
  fsrs_params: FSRSParameters | null
  new_cards_limit: number
  review_limit: number
  rollover_hour: number
  created_at: string
  item_count: number
}
